import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import GdprConsentCheckbox from '../components/GdprConsentCheckbox';
import QuoteRequestSuccess from '../components/QuoteRequestSuccess';
import { isValidFlexiblePhone, PHONE_PLACEHOLDER } from '../utils/phoneValidation';
import '../App.css';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8003').replace(/\/$/, '');

const PARCEL_OPTION = 'Csomagküldés vagy költöztetés/Parcel delivery service or Removals';
const IKEA_OPTION = 'IKEA házhozszállítás/IKEA home delivery';

const IKEA_STORES = [
  'IKEA Örs Vezér tere',
  'IKEA Soroksár',
  'IKEA Budaörs',
];

const ORDER_COUNTRIES = [
  'Magyarország',
  'Ausztria',
  'Belgium',
  'Bulgária',
  'Ciprus',
  'Cseh Köztársaság',
  'Dánia',
  'Észtország',
  'Finnország',
  'Franciaország',
  'Németország',
  'Görögország',
  'Írország',
  'Olaszország',
  'Lettország',
  'Litvánia',
  'Luxembourg',
  'Málta',
  'Hollandia',
  'Lengyelország',
  'Portugália',
  'Románia',
  'Szlovákia',
  'Szlovénia',
  'Spanyolország',
  'Svédország',
  'Egyesült Királyság',
];

function isNonEmpty(s) {
  return String(s || '').trim().length > 0;
}

function isValidEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());
}

const initialForm = {
  refSzam: '',
  refSzam2: '',
  yourName: '',
  yourEmail: '',
  szallitasTipus: PARCEL_OPTION,
  aruhaz: '',
  varos: '',
  honnaniranyitoszam: '',
  orszag: 'Magyarország',
  utcaHonnan: '',
  hazszamHonnan: '',
  feladoNeve: '',
  feladoTelefon: '',
  feladoEmail: '',
  varosHova: '',
  hovaIranyitoszam: '',
  orszagHova: 'Magyarország',
  hovaUtca: '',
  hovaHazszam: '',
  cimzettNeve: '',
  cimzettTelefon: '',
  cimzettEmail: '',
  cegnev: '',
  szemelyNeve: '',
  szamlazasicim: '',
  adoszam: '',
  euAdoszam: '',
  privacyAccepted: false,
};

export default function VeglegesMegrendelesPage() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();

  const [loading, setLoading] = React.useState(!!token);
  const [loadError, setLoadError] = React.useState('');
  const [form, setForm] = React.useState(initialForm);
  const [submitError, setSubmitError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState({});

  React.useEffect(() => {
    if (!token) {
      setLoading(false);
      setLoadError('Hiányzó megrendelési link. Nyisd meg a gombot az árajánlat e-mailben.');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await fetch(`${API_BASE}/api/public/order-prefill?token=${encodeURIComponent(token)}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Nem sikerült betölteni az adatokat.');
        if (cancelled) return;
        const p = data.prefill || {};
        setForm((f) => ({
          ...f,
          refSzam: p.refSzam || '',
          refSzam2: p.refSzam || '',
          yourName: p.yourName || '',
          yourEmail: p.yourEmail || '',
          szallitasTipus: p.szallitasTipus || PARCEL_OPTION,
          varos: p.varos || '',
          honnaniranyitoszam: p.honnaniranyitoszam || '',
          orszag: p.orszag || 'Magyarország',
          utcaHonnan: p.utcaHonnan || '',
          hazszamHonnan: p.hazszamHonnan || '',
          feladoNeve: '',
          feladoTelefon: '',
          feladoEmail: '',
          varosHova: p.varosHova || '',
          hovaIranyitoszam: p.hovaIranyitoszam || '',
          orszagHova: p.orszagHova || 'Magyarország',
          hovaUtca: p.hovaUtca || '',
          hovaHazszam: p.hovaHazszam || '',
          cimzettNeve: '',
          cimzettTelefon: '',
          cimzettEmail: '',
        }));
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Betöltés sikertelen.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSubmitError('');
    setFieldErrors((prev) => {
      if (key === 'refSzam' || key === 'refSzam2') {
        if (!prev.refSzam && !prev.refSzam2) return prev;
        const next = { ...prev };
        delete next.refSzam;
        delete next.refSzam2;
        return next;
      }
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const fieldWrapClass = (key) => {
    const err = fieldErrors[key];
    return `field qf-field${err ? ' order-finalize-field-error' : ''}`;
  };

  const isIkea = form.szallitasTipus === IKEA_OPTION;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError('');
    setFieldErrors({});
    if (!token) return;

    const invalid = {};
    let firstFocusId = null;
    const mark = (key, elementId) => {
      invalid[key] = true;
      if (!firstFocusId) firstFocusId = elementId;
    };

    if (!isNonEmpty(form.refSzam)) mark('refSzam', 'refSzam');
    if (!isNonEmpty(form.refSzam2)) mark('refSzam2', 'refSzam2');
    if (
      isNonEmpty(form.refSzam) &&
      isNonEmpty(form.refSzam2) &&
      form.refSzam.trim() !== form.refSzam2.trim()
    ) {
      invalid.refSzam = true;
      invalid.refSzam2 = true;
      if (!firstFocusId) firstFocusId = 'refSzam';
    }
    if (!isNonEmpty(form.yourName)) mark('yourName', 'yourName');
    if (!isNonEmpty(form.yourEmail) || !isValidEmail(form.yourEmail)) mark('yourEmail', 'yourEmail');
    if (isIkea && !isNonEmpty(form.aruhaz)) mark('aruhaz', 'aruhaz');
    if (!isIkea) {
      if (!isNonEmpty(form.varos)) mark('varos', 'varos');
      if (!isNonEmpty(form.honnaniranyitoszam)) mark('honnaniranyitoszam', 'honnaniranyitoszam');
      if (!isNonEmpty(form.utcaHonnan)) mark('utcaHonnan', 'utcaHonnan');
      if (!isNonEmpty(form.hazszamHonnan)) mark('hazszamHonnan', 'hazszamHonnan');
    }
    if (!isNonEmpty(form.feladoNeve)) mark('feladoNeve', 'feladoNeve');
    if (!isValidFlexiblePhone(form.feladoTelefon)) mark('feladoTelefon', 'feladoTelefon');
    if (!isNonEmpty(form.feladoEmail) || !isValidEmail(form.feladoEmail)) mark('feladoEmail', 'feladoEmail');
    if (!isNonEmpty(form.varosHova)) mark('varosHova', 'varosHova');
    if (!isNonEmpty(form.hovaIranyitoszam)) mark('hovaIranyitoszam', 'hovaIranyitoszam');
    if (!isNonEmpty(form.hovaUtca)) mark('hovaUtca', 'hovaUtca');
    if (!isNonEmpty(form.hovaHazszam)) mark('hovaHazszam', 'hovaHazszam');
    if (!isNonEmpty(form.cimzettNeve)) mark('cimzettNeve', 'cimzettNeve');
    if (!isValidFlexiblePhone(form.cimzettTelefon)) mark('cimzettTelefon', 'cimzettTelefon');
    if (!isNonEmpty(form.szemelyNeve)) mark('szemelyNeve', 'szemelyNeve');
    if (!isNonEmpty(form.szamlazasicim)) mark('szamlazasicim', 'szamlazasicim');
    if (!form.privacyAccepted) mark('privacyAccepted', 'order-gdpr');

    if (Object.keys(invalid).length > 0) {
      setFieldErrors(invalid);
      setSubmitError('Kérjük, töltsd ki a kötelező mezőket (a pirossal jelöltek hiányoznak vagy hibásak).');
      window.setTimeout(() => {
        const el = document.getElementById(firstFocusId || 'refSzam');
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (el && typeof el.focus === 'function' && el.tagName !== 'DIV') el.focus();
      }, 0);
      return;
    }

    setSubmitting(true);
    const honnanParcel = {
      varos: form.varos.trim(),
      honnaniranyitoszam: form.honnaniranyitoszam.trim(),
      orszag: form.orszag,
      utcaHonnan: form.utcaHonnan.trim(),
      hazszamHonnan: form.hazszamHonnan.trim(),
    };
    const honnanPayload = isIkea
      ? {
          varos: form.aruhaz.trim(),
          honnaniranyitoszam: '—',
          orszag: 'Magyarország',
          utcaHonnan: 'IKEA áruház',
          hazszamHonnan: '—',
        }
      : honnanParcel;

    fetch(`${API_BASE}/api/public/order-submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        refSzam: form.refSzam.trim(),
        refSzam2: form.refSzam2.trim(),
        yourName: form.yourName.trim(),
        yourEmail: form.yourEmail.trim(),
        szallitasTipus: form.szallitasTipus,
        aruhaz: form.aruhaz,
        varos: honnanPayload.varos,
        honnaniranyitoszam: honnanPayload.honnaniranyitoszam,
        orszag: honnanPayload.orszag,
        utcaHonnan: honnanPayload.utcaHonnan,
        hazszamHonnan: honnanPayload.hazszamHonnan,
        feladoNeve: form.feladoNeve.trim(),
        feladoTelefon: form.feladoTelefon.trim(),
        feladoEmail: form.feladoEmail.trim(),
        varosHova: form.varosHova.trim(),
        hovaIranyitoszam: form.hovaIranyitoszam.trim(),
        orszagHova: form.orszagHova,
        hovaUtca: form.hovaUtca.trim(),
        hovaHazszam: form.hovaHazszam.trim(),
        cimzettNeve: form.cimzettNeve.trim(),
        cimzettTelefon: form.cimzettTelefon.trim(),
        cimzettEmail: form.cimzettEmail.trim(),
        cegnev: form.cegnev.trim(),
        szemelyNeve: form.szemelyNeve.trim(),
        szamlazasicim: form.szamlazasicim.trim(),
        adoszam: form.adoszam.trim(),
        euAdoszam: form.euAdoszam.trim(),
        adatkezeles: true,
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Küldés sikertelen.');
        setDone(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((err) => {
        setSubmitError(err.message || 'Küldés sikertelen.');
      })
      .finally(() => setSubmitting(false));
  };

  if (loading) {
    return (
      <main className="qf-page order-finalize-page">
        <div className="qf-inner">
          <p>Betöltés…</p>
        </div>
      </main>
    );
  }

  if (loadError && !token) {
    return (
      <main className="qf-page order-finalize-page">
        <div className="qf-inner">
          <h1 className="qf-title">Megrendelés véglegesítése</h1>
          <p className="qf-error-banner">{loadError}</p>
          <p>
            <Link to="/ajanlat/csomagszallitas">Új árajánlatkérés</Link>
          </p>
        </div>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="qf-page order-finalize-page">
        <div className="qf-inner">
          <h1 className="qf-title">Megrendelés véglegesítése</h1>
          <p className="qf-error-banner">{loadError}</p>
          <p>Ha már leadtad a megrendelést, ez a link nem használható újra. Egyéb esetben írj az irodának.</p>
        </div>
      </main>
    );
  }

  if (done) {
    return (
      <main className="qf-page order-finalize-page">
        <div className="qf-inner">
          <QuoteRequestSuccess
            title="Köszönjük a megrendelést!"
            subtitle="A megrendelésedet sikeresen rögzítettük. Hamarosan felvesszük veled a kapcsolatot a megadott elérhetőségeken."
            features={[
              {
                icon: 'user',
                title: 'Megrendelésed sikeresen rögzítve',
                text: 'Köszönjük, hogy elküldted a végleges adatokat.',
              },
              {
                icon: 'phone',
                title: 'Hamarosan egyeztetünk veled',
                text: 'Munkatársunk rövidesen keresni fog.',
              },
              {
                icon: 'mail',
                title: 'Visszaigazolást is küldünk',
                text: 'Kérjük, ellenőrizd a beérkező levelek között is.',
              },
            ]}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="qf-page order-finalize-page">
      <div className="qf-inner order-finalize-inner">
        <h1 className="qf-title">Megrendelés véglegesítése</h1>
        <p className="order-finalize-lead">
          Add meg a számlázáshoz és a fuvarhoz szükséges címadatokat. Az ajánlatkérési számnak egyeznie kell az
          e-mailben kapottal.
        </p>

        <form className="order-finalize-form" onSubmit={handleSubmit} noValidate>
          {submitError ? <p className="qf-error-banner">{submitError}</p> : null}

          <h2 className="order-finalize-h2">Ajánlat és megrendelő</h2>
          <div className="order-finalize-grid">
            <div className={fieldWrapClass('refSzam')}>
              <label htmlFor="refSzam">Ajánlatkérési szám *</label>
              <input
                id="refSzam"
                value={form.refSzam}
                onChange={(e) => update('refSzam', e.target.value)}
                autoComplete="off"
                aria-invalid={fieldErrors.refSzam ? true : undefined}
              />
            </div>
            <div className={fieldWrapClass('refSzam2')}>
              <label htmlFor="refSzam2">Ajánlatkérési szám megerősítése *</label>
              <input
                id="refSzam2"
                value={form.refSzam2}
                onChange={(e) => update('refSzam2', e.target.value)}
                autoComplete="off"
                aria-invalid={fieldErrors.refSzam2 ? true : undefined}
              />
            </div>
            <div className={fieldWrapClass('yourName')}>
              <label htmlFor="yourName">Az Ön neve *</label>
              <input
                id="yourName"
                value={form.yourName}
                onChange={(e) => update('yourName', e.target.value)}
                aria-invalid={fieldErrors.yourName ? true : undefined}
              />
            </div>
            <div className={fieldWrapClass('yourEmail')}>
              <label htmlFor="yourEmail">Az Ön e-mail címe *</label>
              <input
                id="yourEmail"
                type="email"
                value={form.yourEmail}
                onChange={(e) => update('yourEmail', e.target.value)}
                aria-invalid={fieldErrors.yourEmail ? true : undefined}
              />
            </div>
          </div>

          <h2 className="order-finalize-h2">HONNAN</h2>
          <div className="field qf-field">
            <label htmlFor="szallitasTipus">Szállítás típusa *</label>
            <select
              id="szallitasTipus"
              value={form.szallitasTipus}
              onChange={(e) => update('szallitasTipus', e.target.value)}
              required
            >
              <option value={PARCEL_OPTION}>{PARCEL_OPTION}</option>
              <option value={IKEA_OPTION}>{IKEA_OPTION}</option>
            </select>
          </div>

          {isIkea ? (
            <div className={fieldWrapClass('aruhaz')}>
              <label htmlFor="aruhaz">Áruház *</label>
              <select
                id="aruhaz"
                value={form.aruhaz}
                onChange={(e) => update('aruhaz', e.target.value)}
                aria-invalid={fieldErrors.aruhaz ? true : undefined}
              >
                <option value="">— Kérjük, válasszon —</option>
                {IKEA_STORES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {!isIkea ? (
            <>
              <div className="order-finalize-grid">
                <div className={fieldWrapClass('varos')}>
                  <label htmlFor="varos">Város *</label>
                  <input
                    id="varos"
                    value={form.varos}
                    onChange={(e) => update('varos', e.target.value)}
                    aria-invalid={fieldErrors.varos ? true : undefined}
                  />
                </div>
                <div className={fieldWrapClass('honnaniranyitoszam')}>
                  <label htmlFor="honnaniranyitoszam">Irányítószám *</label>
                  <input
                    id="honnaniranyitoszam"
                    value={form.honnaniranyitoszam}
                    onChange={(e) => update('honnaniranyitoszam', e.target.value)}
                    aria-invalid={fieldErrors.honnaniranyitoszam ? true : undefined}
                  />
                </div>
              </div>
              <div className="field qf-field">
                <label htmlFor="orszag">Ország *</label>
                <select id="orszag" value={form.orszag} onChange={(e) => update('orszag', e.target.value)} required>
                  {ORDER_COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="order-finalize-grid">
                <div className={fieldWrapClass('utcaHonnan')}>
                  <label htmlFor="utcaHonnan">Utca *</label>
                  <input
                    id="utcaHonnan"
                    value={form.utcaHonnan}
                    onChange={(e) => update('utcaHonnan', e.target.value)}
                    aria-invalid={fieldErrors.utcaHonnan ? true : undefined}
                  />
                </div>
                <div className={fieldWrapClass('hazszamHonnan')}>
                  <label htmlFor="hazszamHonnan">Házszám *</label>
                  <input
                    id="hazszamHonnan"
                    value={form.hazszamHonnan}
                    onChange={(e) => update('hazszamHonnan', e.target.value)}
                    aria-invalid={fieldErrors.hazszamHonnan ? true : undefined}
                  />
                </div>
              </div>
            </>
          ) : null}

          <h2 className="order-finalize-h2">Feladó adatai</h2>
          <div className="order-finalize-grid">
            <div className={fieldWrapClass('feladoNeve')}>
              <label htmlFor="feladoNeve">Név *</label>
              <input
                id="feladoNeve"
                value={form.feladoNeve}
                onChange={(e) => update('feladoNeve', e.target.value)}
                aria-invalid={fieldErrors.feladoNeve ? true : undefined}
              />
            </div>
            <div className={fieldWrapClass('feladoTelefon')}>
              <label htmlFor="feladoTelefon">Telefon *</label>
              <input
                id="feladoTelefon"
                type="tel"
                value={form.feladoTelefon}
                onChange={(e) => update('feladoTelefon', e.target.value)}
                placeholder={PHONE_PLACEHOLDER}
                aria-invalid={fieldErrors.feladoTelefon ? true : undefined}
              />
            </div>
            <div className={`${fieldWrapClass('feladoEmail')} order-finalize-grid-full`}>
              <label htmlFor="feladoEmail">E-mail *</label>
              <input
                id="feladoEmail"
                type="email"
                value={form.feladoEmail}
                onChange={(e) => update('feladoEmail', e.target.value)}
                aria-invalid={fieldErrors.feladoEmail ? true : undefined}
              />
            </div>
          </div>

          <h2 className="order-finalize-h2">HOVÁ</h2>
          <div className="order-finalize-grid">
            <div className={fieldWrapClass('varosHova')}>
              <label htmlFor="varosHova">Város *</label>
              <input
                id="varosHova"
                value={form.varosHova}
                onChange={(e) => update('varosHova', e.target.value)}
                aria-invalid={fieldErrors.varosHova ? true : undefined}
              />
            </div>
            <div className={fieldWrapClass('hovaIranyitoszam')}>
              <label htmlFor="hovaIranyitoszam">Irányítószám *</label>
              <input
                id="hovaIranyitoszam"
                value={form.hovaIranyitoszam}
                onChange={(e) => update('hovaIranyitoszam', e.target.value)}
                aria-invalid={fieldErrors.hovaIranyitoszam ? true : undefined}
              />
            </div>
          </div>
          <div className="field qf-field">
            <label htmlFor="orszagHova">Ország *</label>
            <select
              id="orszagHova"
              value={form.orszagHova}
              onChange={(e) => update('orszagHova', e.target.value)}
              required
            >
              {ORDER_COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="order-finalize-grid">
            <div className={fieldWrapClass('hovaUtca')}>
              <label htmlFor="hovaUtca">Utca *</label>
              <input
                id="hovaUtca"
                value={form.hovaUtca}
                onChange={(e) => update('hovaUtca', e.target.value)}
                aria-invalid={fieldErrors.hovaUtca ? true : undefined}
              />
            </div>
            <div className={fieldWrapClass('hovaHazszam')}>
              <label htmlFor="hovaHazszam">Házszám *</label>
              <input
                id="hovaHazszam"
                value={form.hovaHazszam}
                onChange={(e) => update('hovaHazszam', e.target.value)}
                aria-invalid={fieldErrors.hovaHazszam ? true : undefined}
              />
            </div>
          </div>

          <h2 className="order-finalize-h2">Címzett</h2>
          <div className="order-finalize-grid">
            <div className={fieldWrapClass('cimzettNeve')}>
              <label htmlFor="cimzettNeve">Név *</label>
              <input
                id="cimzettNeve"
                value={form.cimzettNeve}
                onChange={(e) => update('cimzettNeve', e.target.value)}
                aria-invalid={fieldErrors.cimzettNeve ? true : undefined}
              />
            </div>
            <div className={fieldWrapClass('cimzettTelefon')}>
              <label htmlFor="cimzettTelefon">Telefon *</label>
              <input
                id="cimzettTelefon"
                type="tel"
                value={form.cimzettTelefon}
                onChange={(e) => update('cimzettTelefon', e.target.value)}
                placeholder={PHONE_PLACEHOLDER}
                aria-invalid={fieldErrors.cimzettTelefon ? true : undefined}
              />
            </div>
            <div className="field qf-field order-finalize-grid-full">
              <label htmlFor="cimzettEmail">E-mail</label>
              <input
                id="cimzettEmail"
                type="email"
                value={form.cimzettEmail}
                onChange={(e) => update('cimzettEmail', e.target.value)}
              />
            </div>
          </div>

          <h2 className="order-finalize-h2">Számlázás</h2>
          <div className="order-finalize-grid">
            <div className={fieldWrapClass('szemelyNeve')}>
              <label htmlFor="szemelyNeve">Magánszemély neve *</label>
              <input
                id="szemelyNeve"
                value={form.szemelyNeve}
                onChange={(e) => update('szemelyNeve', e.target.value)}
                aria-invalid={fieldErrors.szemelyNeve ? true : undefined}
              />
            </div>
            <div className="field qf-field">
              <label htmlFor="cegnev">Cégnév</label>
              <input id="cegnev" value={form.cegnev} onChange={(e) => update('cegnev', e.target.value)} />
            </div>
          </div>
          <div className={fieldWrapClass('szamlazasicim')}>
            <label htmlFor="szamlazasicim">Számlázási cím *</label>
            <input
              id="szamlazasicim"
              value={form.szamlazasicim}
              onChange={(e) => update('szamlazasicim', e.target.value)}
              aria-invalid={fieldErrors.szamlazasicim ? true : undefined}
            />
          </div>
          <div className="order-finalize-grid">
            <div className="field qf-field">
              <label htmlFor="adoszam">Adószám (cég esetén)</label>
              <input
                id="adoszam"
                value={form.adoszam}
                onChange={(e) => update('adoszam', e.target.value)}
                placeholder="12345678-00-00"
              />
            </div>
            <div className="field qf-field">
              <label htmlFor="euAdoszam">EU adószám</label>
              <input
                id="euAdoszam"
                value={form.euAdoszam}
                onChange={(e) => update('euAdoszam', e.target.value)}
                placeholder="HU12345678"
              />
            </div>
          </div>

          <div
            className={
              fieldErrors.privacyAccepted ? 'order-finalize-consent order-finalize-field-error' : 'order-finalize-consent'
            }
          >
            <GdprConsentCheckbox
              id="order-gdpr"
              checked={form.privacyAccepted}
              onChange={(v) => update('privacyAccepted', v)}
            />
          </div>

          <div className="order-finalize-actions">
            <button type="submit" className="primary-btn" disabled={submitting}>
              {submitting ? 'Küldés…' : 'Adatok elküldése'}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
